import { useEffect, useState } from "react";
import { AlignJustify, Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { APPROVAL_STATUS_LABELS, type ApprovalStatus } from "@/lib/api-types";
import type { WatchComment, WatchVersion } from "@/hooks/use-watch";
import { useUpdateWatchApproval, useVideoViews } from "@/hooks/use-watch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CommentList } from "./comment-list";
import { CommentComposer } from "./comment-composer";

/**
 * Comment ordering for the right panel.
 *
 *   "timecode" — default, matches Frame.io. Roots ordered by timestampSeconds
 *                ASC (nulls / no-timestamp comments sink to the bottom), then
 *                createdAt ASC as tiebreak.
 *   "newest"   — createdAt DESC.
 *   "oldest"   — createdAt ASC.
 *
 * Sequence numbers (#N) are ALWAYS assigned by root-createdAt-asc regardless
 * of sort — a comment's identity stays stable across sort toggles.
 */
export type CommentSort = "timecode" | "newest" | "oldest";
const SORT_STORAGE_KEY = "watch:comment-sort";

function readStoredSort(): CommentSort {
  if (typeof window === "undefined") return "timecode";
  const raw = window.localStorage.getItem(SORT_STORAGE_KEY);
  if (raw === "newest" || raw === "oldest" || raw === "timecode") return raw;
  return "timecode";
}

const SORT_LABELS: Record<CommentSort, string> = {
  timecode: "Timecode",
  newest: "Newest first",
  oldest: "Oldest first",
};

type Props = {
  mode: "workspace" | "guest" | "share";
  currentVersion: WatchVersion | null;
  comments: WatchComment[];
  /** Video id / share token used for hook invalidation on comment mutations. */
  watchKey: string | undefined;
  /** Seek callback — timecode chip click routes here. */
  onSeek: (seconds: number) => void;
  /** Live-time reader — composer reads this to stamp new comments. */
  getCurrentTime: () => number;
  /** Shared selection with the video-player timeline. Card click writes back
      through onSelectComment; a change from the timeline scrolls the card
      into view. */
  selectedCommentId?: string | null;
  onSelectComment?: (id: string) => void;
  /** Asset type of the version being viewed. Image hides all time UI:
   *  no timecode chip on composer, no timecode chip on comment cards,
   *  timestamp toggle removed, composer sends timestampSeconds:null. */
  assetType?: "video" | "audio" | "image";
};

/**
 * Right panel (Task 9). Shell only — the Comments list body arrives in Task 10,
 * the Comments composer in Task 11, and the Fields tab bodies in Task 15.
 *
 * Header content per user spec:
 *   - Tab bar: Comments | Fields
 *   - Under Comments: "All comments" + Newest/Oldest sort toggle only.
 *     CUT: Filter, Search, More (⋯) icons from the Frame.io reference.
 */
export function RightPanel({
  mode,
  currentVersion,
  comments,
  watchKey,
  onSeek,
  getCurrentTime,
  selectedCommentId = null,
  onSelectComment,
  assetType,
}: Props) {
  const [tab, setTab] = useState<"comments" | "fields">("comments");
  const [sort, setSort] = useState<CommentSort>(() => readStoredSort());

  // Persist sort choice across page reloads / new watch sessions.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(SORT_STORAGE_KEY, sort);
  }, [sort]);

  return (
    <div className="flex h-full flex-col">
      {/* Tab bar — segmented pill matching the Frame.io reference. */}
      <div className="shrink-0 p-3 pb-2">
        <div className="flex items-center gap-0.5 rounded-lg bg-secondary/60 p-0.5">
          <TabButton active={tab === "comments"} onClick={() => setTab("comments")}>
            Comments
          </TabButton>
          <TabButton active={tab === "fields"} onClick={() => setTab("fields")}>
            Fields
          </TabButton>
        </div>
      </div>

      {/* Tab body */}
      {tab === "comments" ? (
        <CommentsTab
          mode={mode}
          comments={comments}
          sort={sort}
          onSortChange={setSort}
          watchKey={watchKey}
          currentVersionId={currentVersion?.id ?? null}
          onSeek={onSeek}
          getCurrentTime={getCurrentTime}
          selectedCommentId={selectedCommentId}
          onSelectComment={onSelectComment}
          assetType={assetType ?? currentVersion?.assetType}
        />
      ) : (
        <FieldsTab mode={mode} currentVersion={currentVersion} watchKey={watchKey} />
      )}
    </div>
  );
}

// ── Tab bar button ───────────────────────────────────────────────────────────

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
        active
          ? "bg-background text-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

// ── Comments tab ─────────────────────────────────────────────────────────────

function CommentsTab({
  mode,
  comments,
  sort,
  onSortChange,
  watchKey,
  currentVersionId,
  onSeek,
  getCurrentTime,
  selectedCommentId,
  onSelectComment,
  assetType,
}: {
  mode: "workspace" | "guest" | "share";
  comments: WatchComment[];
  sort: CommentSort;
  onSortChange: (next: CommentSort) => void;
  watchKey: string | undefined;
  currentVersionId: string | null;
  onSeek: (seconds: number) => void;
  getCurrentTime: () => number;
  selectedCommentId: string | null;
  onSelectComment?: (id: string) => void;
  assetType: "video" | "audio" | "image" | undefined;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Header row: "All comments" + sort toggle only. */}
      <div className="flex shrink-0 items-center justify-between px-4 py-3">
        <div className="text-sm font-medium text-foreground">All comments</div>
        <SortToggle value={sort} onChange={onSortChange} />
      </div>

      <CommentList
        mode={mode}
        comments={comments}
        sort={sort}
        watchKey={watchKey}
        currentVersionId={currentVersionId}
        onSeek={onSeek}
        selectedCommentId={selectedCommentId}
        onSelectComment={onSelectComment}
        assetType={assetType}
      />

      <CommentComposer
        mode={mode}
        watchKey={watchKey}
        currentVersionId={currentVersionId}
        getCurrentTime={getCurrentTime}
        assetType={assetType}
      />
    </div>
  );
}

function SortToggle({
  value,
  onChange,
}: {
  value: CommentSort;
  onChange: (next: CommentSort) => void;
}) {
  // 3-line icon → dropdown with three options. Matches the Frame.io reference
  // (Timecode default, plus Newest / Oldest).
  const options: CommentSort[] = ["timecode", "newest", "oldest"];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Sort comments"
        title={`Sort: ${SORT_LABELS[value]}`}
        className="grid h-7 w-7 place-items-center rounded-md border border-border bg-secondary/40 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
      >
        <AlignJustify className="h-3.5 w-3.5" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        {options.map((opt) => (
          <DropdownMenuItem
            key={opt}
            onSelect={() => onChange(opt)}
            className="flex items-center justify-between"
          >
            <span>{SORT_LABELS[opt]}</span>
            {value === opt && <Check className="h-3.5 w-3.5 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ── Fields tab ───────────────────────────────────────────────────────────────

function FieldsTab({
  mode,
  currentVersion,
  watchKey,
}: {
  mode: "workspace" | "guest" | "share";
  currentVersion: WatchVersion | null;
  watchKey: string | undefined;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-4">
      <div className="mb-1 text-lg font-semibold text-foreground">
        {currentVersion?.title || "Untitled"}
      </div>
      <div className="mb-6 text-xs text-muted-foreground">
        Created {formatCreated(currentVersion?.createdAt)}
      </div>

      {/* Seen By — workspace-only per spec. Guests don't see who else viewed. */}
      {mode === "workspace" && <SeenBySection videoId={currentVersion?.id} />}

      {/* Status dropdown — both modes. Writes to the version being viewed. */}
      <StatusSection
        mode={mode}
        currentVersion={currentVersion}
        watchKey={watchKey}
      />
    </div>
  );
}

function SeenBySection({ videoId }: { videoId: string | undefined }) {
  const { data, isLoading, isError } = useVideoViews(videoId);

  return (
    <section className="mb-6">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Seen By
      </h3>
      {isLoading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading…
        </div>
      ) : isError ? (
        <div className="text-xs text-destructive">Unable to load viewers</div>
      ) : !data || data.viewers.length === 0 ? (
        <div className="text-xs text-muted-foreground">No one has viewed this video yet.</div>
      ) : (
        <ul className="space-y-2">
          {data.viewers.map((v, i) => (
            <li key={i} className="flex items-center gap-2">
              <div
                className={cn(
                  "grid h-6 w-6 shrink-0 place-items-center rounded-full text-[10px] font-semibold uppercase",
                  v.type === "guest"
                    ? "bg-muted text-muted-foreground"
                    : "bg-primary/20 text-primary",
                )}
              >
                {initials(v.name)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-foreground">{v.name}</div>
                {v.type === "guest" && v.email && v.email !== v.name && (
                  <div className="truncate text-[11px] text-muted-foreground">{v.email}</div>
                )}
                <div className="text-[11px] text-muted-foreground">
                  {v.type === "guest" ? "Guest" : v.role || "Workspace member"} · {relativeAgo(v.viewedAt)}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function StatusSection({
  mode,
  currentVersion,
  watchKey,
}: {
  mode: "workspace" | "guest" | "share";
  currentVersion: WatchVersion | null;
  watchKey: string | undefined;
}) {
  const update = useUpdateWatchApproval(mode, watchKey);
  const current = currentVersion?.approvalStatus ?? "in_review";
  const orderedStatuses: ApprovalStatus[] = ["in_review", "changes_requested", "approved"];

  function change(next: ApprovalStatus) {
    if (!currentVersion?.id || next === current) return;
    update.mutate({ videoId: currentVersion.id, approvalStatus: next });
  }

  return (
    <section>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Status
      </h3>
      <DropdownMenu>
        <DropdownMenuTrigger
          disabled={!currentVersion?.id || update.isPending}
          className={cn(
            "flex w-full items-center justify-between rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground transition-colors hover:bg-secondary/40",
            update.isPending && "opacity-70",
          )}
        >
          <span className="flex items-center gap-2">
            <StatusDot status={current} />
            {APPROVAL_STATUS_LABELS[current]}
          </span>
          {update.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
          ) : (
            <span className="text-xs text-muted-foreground">Change</span>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-52">
          {orderedStatuses.map((s) => (
            <DropdownMenuItem key={s} onSelect={() => change(s)}>
              <StatusDot status={s} />
              <span>{APPROVAL_STATUS_LABELS[s]}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {update.error && (
        <div className="mt-2 text-xs text-destructive">
          {(update.error as Error).message || "Status change failed"}
        </div>
      )}
    </section>
  );
}

function StatusDot({ status }: { status: ApprovalStatus }) {
  const color =
    status === "approved"
      ? "bg-emerald-500"
      : status === "changes_requested"
      ? "bg-amber-500"
      : "bg-muted-foreground";
  return <span className={cn("h-2 w-2 shrink-0 rounded-full", color)} />;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function relativeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffSec = Math.floor((Date.now() - then) / 1000);
  if (diffSec < 60) return "just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatCreated(iso: string | undefined | null): string {
  if (!iso) return "recently";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "recently";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
